import React, { useState } from 'react';
import { TaskComment } from '../types/task';
import { getTaskService } from '../services/sharepointHelpers';
import { getAuthService } from '../services/authService';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Textarea,
  Label,
  Avatar,
} from '@fluentui/react-components';
import {
  SendRegular,
  ChatRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  commentInput: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: '20px',
  },
  inputHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  textareaContainer: {
    marginBottom: '16px',
  },
  textarea: {
    minHeight: '100px',
    resize: 'vertical',
    width: '100%',
  },
  mentionedUsers: {
    marginBottom: '16px',
  },
  mentionedLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: '8px',
  },
  mentionedTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  mentionedTag: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  availableUsers: {
    marginBottom: '16px',
  },
  availableLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: '8px',
  },
  availableTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  availableTag: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  submitButton: {
    alignSelf: 'flex-end',
  },
  commentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  emptyDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  commentCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: '16px',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
      boxShadow: tokens.shadow4,
    },
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  avatar: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  commentMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  authorName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  commentDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  commentText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  mentionHighlight: {
    color: tokens.colorPaletteBlueForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  commentMentions: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  mentionsLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: '6px',
  },
  mentionsTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  mentionTag: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightMedium,
  },
});

interface TaskCommentThreadProps {
  taskId: string;
  comments: TaskComment[];
  onCommentAdded: (comment: TaskComment) => void;
}

const TaskCommentThread: React.FC<TaskCommentThreadProps> = ({
  taskId,
  comments,
  onCommentAdded
}) => {
  const styles = useStyles();
  const [newComment, setNewComment] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState<{ id: string; name: string; email: string; role: string }[]>([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState<{ id: string; name: string; email: string; role: string }[]>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  const taskService = getTaskService(getAuthService());

  // Load users for @mentions (lazy load - only when needed)
  React.useEffect(() => {
    // Only load users if we're showing mention suggestions
    if (showMentionSuggestions && users.length === 0) {
      const loadUsers = async () => {
        try {
          const userList = await taskService.getUsers();
          setUsers(userList);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      };

      loadUsers();
    }
  }, [taskService, showMentionSuggestions, users.length]);

  // Filter users based on mention query
  React.useEffect(() => {
    if (mentionQuery && users.length > 0) {
      const query = mentionQuery.toLowerCase();
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
      ).slice(0, 10); // Limit to 10 suggestions
      setFilteredUsers(filtered);
      setSelectedMentionIndex(0); // Reset selection when filtered users change
    } else {
      setFilteredUsers([]);
      setSelectedMentionIndex(0);
    }
  }, [mentionQuery, users]);

  // Handle comment submission
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      
      // Extract mentioned users from comment text
      const mentioned = extractMentions(newComment);
      
      const comment = await taskService.addTaskComment(taskId, newComment, mentioned);
      onCommentAdded(comment);
      
      setNewComment('');
      setMentionedUsers([]);
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Extract @mentions from comment text
  const extractMentions = (text: string): string[] => {
    const mentionRegex = /@(\w+)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      const mentionedName = match[1];
      const user = users.find(u => 
        u.name.toLowerCase().includes(mentionedName.toLowerCase()) ||
        u.email.toLowerCase().includes(mentionedName.toLowerCase())
      );
      if (user) {
        mentions.push(user.name);
      }
    }
    
    return mentions;
  };

  // Handle @mention input
  const handleCommentChange = (value: string, cursorPosition?: number) => {
    setNewComment(value);
    
    // Check for @mentions
    const mentions = extractMentions(value);
    setMentionedUsers(mentions);
    
    // Check if user is typing @mention
    const pos = cursorPosition !== undefined ? cursorPosition : value.length;
    const textBeforeCursor = value.substring(0, pos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Get text after @
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's a space or newline after @ (meaning @mention is complete)
      const hasSpaceOrNewline = textAfterAt.includes(' ') || textAfterAt.includes('\n');
      
      if (!hasSpaceOrNewline) {
        // User is typing a mention - extract the query
        const query = textAfterAt.trim();
        setMentionQuery(query);
        setMentionPosition(lastAtIndex);
        setShowMentionSuggestions(true);
        return;
      }
    }
    
    // Hide suggestions if not typing a mention
    setShowMentionSuggestions(false);
    setMentionQuery('');
  };

  // Handle selecting a user from mention suggestions
  const handleSelectMention = (userName: string) => {
    const textBeforeAt = newComment.substring(0, mentionPosition);
    const textAfterAt = newComment.substring(mentionPosition + 1);
    // Find where the current mention query ends (before space or end of text)
    const queryEndIndex = textAfterAt.search(/[\s\n]/);
    const textAfterMention = queryEndIndex !== -1 
      ? textAfterAt.substring(queryEndIndex)
      : '';
    
    const newText = `${textBeforeAt}@${userName}${textAfterMention}`;
    setNewComment(newText);
    setShowMentionSuggestions(false);
    setMentionQuery('');
    
    // Update mentioned users
    const mentions = extractMentions(newText);
    setMentionedUsers(mentions);
    
    // Focus back on textarea
    const textarea = document.getElementById('comment') as HTMLTextAreaElement;
    if (textarea) {
      setTimeout(() => {
        textarea.focus();
        // Set cursor position after the inserted mention
        const cursorPos = textBeforeAt.length + 1 + userName.length;
        textarea.setSelectionRange(cursorPos, cursorPos);
      }, 0);
    }
  };

  // Format comment text with @mentions
  const formatCommentText = (text: string) => {
    return text.replace(/@(\w+)/g, (match, name) => {
      const user = users.find(u => 
        u.name.toLowerCase().includes(name.toLowerCase()) ||
        u.email.toLowerCase().includes(name.toLowerCase())
      );
      if (user) {
        return `<span class="${styles.mentionHighlight}">@${user.name}</span>`;
      }
      return match;
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={styles.container}>
      {/* Comment Input */}
      <div className={styles.commentInput}>
        <form onSubmit={handleSubmitComment}>
          <div className={styles.inputHeader}>
            <ChatRegular />
            <Label htmlFor="comment">Add a comment</Label>
          </div>
          
          <div className={styles.textareaContainer} style={{ position: 'relative' }}>
            <Textarea
              id="comment"
              value={newComment}
              onChange={(e, data) => {
                const textarea = e.target as HTMLTextAreaElement;
                const cursorPos = textarea.selectionStart;
                handleCommentChange(data.value, cursorPos);
              }}
              onKeyDown={(e) => {
                if (showMentionSuggestions && filteredUsers.length > 0) {
                  // Handle Enter key to select highlighted suggestion
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSelectMention(filteredUsers[selectedMentionIndex].name);
                    return;
                  }
                  // Handle Arrow Down to navigate down
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedMentionIndex(prev => 
                      prev < filteredUsers.length - 1 ? prev + 1 : prev
                    );
                    return;
                  }
                  // Handle Arrow Up to navigate up
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
                    return;
                  }
                }
                // Handle Escape to close suggestions
                if (showMentionSuggestions && e.key === 'Escape') {
                  setShowMentionSuggestions(false);
                  setMentionQuery('');
                }
              }}
              placeholder="Type your comment here... Use @username to mention someone"
              disabled={submitting}
              className={styles.textarea}
            />
            
            {/* Mention Suggestions Dropdown */}
            {showMentionSuggestions && filteredUsers.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                backgroundColor: tokens.colorNeutralBackground1,
                border: `1px solid ${tokens.colorNeutralStroke1}`,
                borderRadius: tokens.borderRadiusMedium,
                boxShadow: tokens.shadow16,
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                marginBottom: '4px',
              }}>
                {filteredUsers.map((user, index) => (
                  <div
                    key={user.id}
                    onClick={() => handleSelectMention(user.name)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: index < filteredUsers.length - 1 ? `1px solid ${tokens.colorNeutralStroke2}` : 'none',
                      backgroundColor: index === selectedMentionIndex 
                        ? tokens.colorNeutralBackground2 
                        : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={() => {
                      setSelectedMentionIndex(index);
                    }}
                  >
                    <div style={{ fontWeight: tokens.fontWeightSemibold }}>
                      {user.name}
                    </div>
                    <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
                      {user.role} {user.email ? `• ${user.email}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Mentioned Users Display */}
          {mentionedUsers.length > 0 && (
            <div className={styles.mentionedUsers}>
              <Text className={styles.mentionedLabel}>Mentioned users:</Text>
              <div className={styles.mentionedTags}>
                {mentionedUsers.map((user, index) => (
                  <span key={index} className={styles.mentionedTag}>
                    @{user}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <Button
            type="submit"
            appearance="primary"
            icon={<SendRegular />}
            disabled={!newComment.trim() || submitting}
            className={styles.submitButton}
          >
            {submitting ? 'Posting...' : 'Post Comment'}
          </Button>
        </form>
      </div>

      {/* Comments List */}
      <div className={styles.commentsList}>
        {comments.length === 0 ? (
          <div className={styles.emptyState}>
            <ChatRegular className={styles.emptyIcon} />
            <Text className={styles.emptyTitle}>No comments yet</Text>
            <Text className={styles.emptyDescription}>
              Be the first to add a comment to this task.
            </Text>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className={styles.commentCard}>
              <div className={styles.commentHeader}>
                <Avatar
                  className={styles.avatar}
                  size={32}
                >
                  {comment.authorDisplayName?.charAt(0) || comment.createdBy.charAt(0)}
                </Avatar>
                <div className={styles.commentMeta}>
                  <Text className={styles.authorName}>
                    {comment.authorDisplayName || comment.createdBy}
                  </Text>
                  <Text className={styles.commentDate}>
                    {formatDate(comment.created)}
                  </Text>
                </div>
              </div>
              
              <div 
                className={styles.commentText}
                dangerouslySetInnerHTML={{ __html: formatCommentText(comment.comment) }}
              />
              
              {/* Mentioned Users */}
              {comment.mentioned && comment.mentioned.length > 0 && (
                <div className={styles.commentMentions}>
                  <Text className={styles.mentionsLabel}>Mentioned:</Text>
                  <div className={styles.mentionsTags}>
                    {comment.mentioned.map((user, index) => (
                      <span key={index} className={styles.mentionTag}>
                        @{user}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskCommentThread; 